import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Flag } from "lucide-react";

import { api, photoUri } from "@/lib/api";
import { REPORT_REASONS } from "@/lib/constants";
import { fmtDate } from "@/lib/format";
import { AdminPage, Card, ChipRow, Gate, Meta, Pager, reportTone } from "@/components/admin-ui";
import { EmptyState, ErrorState, Loading, Pill } from "@/components/ui";

export default function AdminReports() {
  return <Gate perm="reports" testId="admin-reports-forbidden"><Inner /></Gate>;
}

function Inner() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("open");
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["admin-reports", status, page],
    queryFn: () => api(`/admin/reports?page=${page}${status ? `&status=${status}` : ""}`),
  });
  const c = data?.counts ?? {};
  const opts = [
    { id: "open", label: `Open ${c.open ?? ""}` }, { id: "in_review", label: `In review ${c.in_review ?? ""}` },
    { id: "resolved", label: `Resolved ${c.resolved ?? ""}` }, { id: "dismissed", label: `Dismissed ${c.dismissed ?? ""}` }, { id: "", label: "All" },
  ];
  return (
    <AdminPage title="Reports" testId="admin-reports-screen">
      <ChipRow options={opts} value={status} onChange={(v) => { setStatus(v); setPage(1); }} testIdPrefix="admin-reports-status" />
      {isLoading ? <Loading /> : isError ? <ErrorState testId="admin-reports-error" message={error.message} onRetry={() => refetch()} /> : data.reports.length ? (
        <>
          <div className="grid gap-3 md:grid-cols-2">
            {data.reports.map((r) => (
              <Card key={r.id} testId={`admin-report-row-${r.id}`} onClick={() => navigate(`/admin/report/${r.id}`)}>
                <div className="flex items-center gap-3">
                  <img src={photoUri(r.photo_url ?? r.reported.main_photo_url)} alt="" className="h-14 w-14 rounded-xl bg-[#F4EBE6] object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="font-bold">{r.reported.display_name} <span className="font-normal text-primary">· {REPORT_REASONS[r.reason]}{r.photo_id ? " (photo)" : ""}</span></p>
                    <Meta>Reported by {r.reporter.display_name} · {fmtDate(r.created_at)}</Meta>
                    <div className="mt-1.5 flex gap-1.5">
                      <Pill label={r.status.replace("_", " ")} tone={reportTone(r.status)} />
                      <Pill testId={`admin-report-count-${r.id}`} label={`${r.reports_against} report${r.reports_against === 1 ? "" : "s"} against`} tone="rose" />
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
          <Pager testId="admin-reports-pager" page={data.page} pages={data.pages} total={data.total} onChange={setPage} />
        </>
      ) : <EmptyState testId="admin-reports-empty" icon={Flag} title="No reports here" />}
    </AdminPage>
  );
}
